import * as THREE from 'three';
import type { World } from '../sim/world';
import { patchMaterial } from './atmosphere';
import { box, lathe, V } from './ship/geom';
import { forInstancing, type ShipMaterials } from './materials/materials';
import { commitInstances } from './gpu';

// The world around the ship: gulls and flotsam. Deterministic (seeded) where it matters for layout.

function gullGeometry(): THREE.BufferGeometry {
  // Body along +x, wings along ±z. Vertex colour R marks wing flex (0..1).
  const p: number[] = [];
  const f: number[] = [];
  const tri = (a: number[], b: number[], c: number[], fa: number, fb: number, fc: number) => {
    p.push(...a, ...b, ...c);
    f.push(fa, fb, fc);
  };
  // Body.
  tri([0.22, 0, 0], [-0.25, 0.02, 0.05], [-0.25, 0.02, -0.05], 0, 0, 0);
  tri([0.22, 0, 0], [-0.25, -0.04, 0], [-0.25, 0.02, 0.05], 0, 0, 0);
  tri([0.22, 0, 0], [-0.25, 0.02, -0.05], [-0.25, -0.04, 0], 0, 0, 0);
  tri([-0.25, 0.02, 0.05], [-0.36, 0.0, 0], [-0.25, 0.02, -0.05], 0, 0, 0);
  for (const s of [1, -1]) {
    tri([0.08, 0, 0.03 * s], [-0.08, 0, 0.03 * s], [0.02, 0, 0.35 * s], 0, 0, 0.5);
    tri([-0.08, 0, 0.03 * s], [-0.1, 0, 0.35 * s], [0.02, 0, 0.35 * s], 0, 0.5, 0.5);
    tri([0.02, 0, 0.35 * s], [-0.1, 0, 0.35 * s], [-0.12, 0, 0.62 * s], 0.5, 0.5, 1);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(p, 3));
  g.setAttribute('aFlex', new THREE.Float32BufferAttribute(f, 1));
  g.computeVertexNormals();
  return g;
}

interface Gull {
  pos: THREE.Vector3;
  vel: THREE.Vector3;
  phase: number;
  flap: number;
  orbitR: number;
  orbitH: number;
  orbitSpeed: number;
  angle: number;
}

export class Environment {
  readonly group = new THREE.Group();
  private readonly gulls: Gull[] = [];
  private readonly gullMesh: THREE.InstancedMesh;
  private readonly gullUniforms = { uFlapTime: { value: 0 } };
  private readonly flotsamMeshes: Record<string, THREE.InstancedMesh> = {};
  private time = 0;

  constructor(private readonly world: World, mats: ShipMaterials) {
    // Gulls.
    const gullMat = patchMaterial(new THREE.MeshStandardMaterial({ color: new THREE.Color(0.85, 0.86, 0.85), roughness: 0.8, side: THREE.DoubleSide }), (sh) => {
      Object.assign(sh.uniforms, this.gullUniforms);
      sh.vertexShader = 'attribute float aFlex;\nuniform float uFlapTime;\n' + sh.vertexShader.replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        {
          float ph = 0.0;
          #ifdef USE_INSTANCING
            ph = instanceMatrix[3][0] * 0.37 + instanceMatrix[3][2] * 0.21;
          #endif
          float flap = sin(uFlapTime * 9.0 + ph) * step(0.5, fract(uFlapTime * 0.13 + ph * 0.1));
          transformed.y += aFlex * flap * 0.28 + aFlex * aFlex * 0.05;
        }`,
      );
    }, 'gull');
    const gg = gullGeometry();
    this.gullMesh = new THREE.InstancedMesh(gg, gullMat, 22);
    this.gullMesh.castShadow = true;
    this.gullMesh.frustumCulled = false;
    this.group.add(this.gullMesh);
    for (let i = 0; i < 22; i++) {
      this.gulls.push({
        pos: V((Math.random() - 0.5) * 80, 25 + Math.random() * 20, (Math.random() - 0.5) * 80),
        vel: V(0, 0, 0), phase: Math.random() * 10, flap: 0,
        orbitR: 18 + Math.random() * 45, orbitH: 18 + Math.random() * 30, orbitSpeed: (0.12 + Math.random() * 0.1) * (Math.random() < 0.5 ? -1 : 1),
        angle: Math.random() * Math.PI * 2,
      });
    }

    // Flotsam.
    const kinds: Record<string, [THREE.BufferGeometry, THREE.Material]> = {
      plank: [box(2.4, 0.08, 0.3), mats.timber],
      barrel: [lathe([[0.001, -0.45], [0.3, -0.45], [0.36, 0], [0.3, 0.45], [0.001, 0.45]], 12), mats.timber],
      crate: [box(0.8, 0.8, 0.8), mats.timber],
      spar: [new THREE.CylinderGeometry(0.14, 0.18, 6, 8).rotateZ(Math.PI / 2), mats.spar],
      weed: [new THREE.CircleGeometry(1.2, 9).rotateX(-Math.PI / 2), patchMaterial(new THREE.MeshStandardMaterial({ color: new THREE.Color(0.18, 0.14, 0.04), roughness: 0.9, side: THREE.DoubleSide }), undefined, 'weed')],
      canvas: [crumpledCanvas(), mats.canvas],
    };
    for (const [k, [geo, mat]] of Object.entries(kinds)) {
      const im = new THREE.InstancedMesh(geo, forInstancing(mat), 160);
      im.castShadow = true;
      im.receiveShadow = true;
      im.frustumCulled = false;
      im.count = 0;
      this.flotsamMeshes[k] = im;
      this.group.add(im);
    }
  }

  /** The ship jumped: her gulls come with her. */
  carry(dx: number, dy: number, dz: number): void {
    for (const g of this.gulls) g.pos.add(V(dx, dy, dz));
  }

  randomGull(): THREE.Vector3 {
    return this.gulls[Math.floor(Math.random() * this.gulls.length)]!.pos.clone();
  }

  /** `sees`: is a world-space sphere in frame? Flotsam out of it isn't drawn. */
  update(dt: number, shipPos: THREE.Vector3, sees: (x: number, y: number, z: number, r: number) => boolean = () => true): void {
    this.time += dt;
    this.gullUniforms.uFlapTime.value = this.time;
    const w = this.world;
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const s = new THREE.Vector3(1, 1, 1);
    // Gulls wheel about the ship, riding the wind over the rig.
    this.gulls.forEach((g, i) => {
      g.angle += g.orbitSpeed * dt;
      const tx = shipPos.x + Math.cos(g.angle) * g.orbitR + Math.sin(this.time * 0.1 + i) * 8;
      const tz = shipPos.z + Math.sin(g.angle) * g.orbitR;
      const ty = shipPos.y + g.orbitH + Math.sin(this.time * 0.3 + i) * 4;
      const target = V(tx, ty, tz);
      const desired = target.sub(g.pos).multiplyScalar(0.6);
      const sp = desired.length();
      if (sp > 14) desired.multiplyScalar(14 / sp);
      g.vel.lerp(desired, 1 - Math.exp(-dt * 0.8));
      g.pos.addScaledVector(g.vel, dt);
      const v = g.vel.clone();
      if (v.lengthSq() < 0.01) v.set(1, 0, 0);
      const bank = THREE.MathUtils.clamp(-g.orbitSpeed * 4, -0.6, 0.6);
      q.setFromUnitVectors(V(1, 0, 0), v.normalize());
      q.multiply(new THREE.Quaternion().setFromAxisAngle(V(1, 0, 0), bank));
      m.compose(g.pos, q, s.set(1.6, 1.6, 1.6));
      this.gullMesh.setMatrixAt(i, m);
    });
    this.gullMesh.instanceMatrix.needsUpdate = true;

    const counts: Record<string, number> = {};
    for (const k of Object.keys(this.flotsamMeshes)) counts[k] = 0;
    for (const f of w.flotsam.items) {
      const im = this.flotsamMeshes[f.kind]!;
      if (counts[f.kind]! >= 160) continue;
      if (!sees(f.pos.x, f.pos.y, f.pos.z, 4 * f.size)) continue;
      const i = counts[f.kind]!++;
      q.set(f.rot.x, f.rot.y, f.rot.z, f.rot.w);
      m.compose(V(f.pos.x, f.pos.y, f.pos.z), q, s.set(f.size, f.size, f.size));
      im.setMatrixAt(i, m);
    }
    for (const [k, im] of Object.entries(this.flotsamMeshes)) commitInstances(im, counts[k]!);

  }
}

/** A torn sheet of sailcloth floating in folds. */
function crumpledCanvas(): THREE.BufferGeometry {
  const g = new THREE.PlaneGeometry(3.2, 2.4, 8, 6);
  g.rotateX(-Math.PI / 2);
  const p = g.getAttribute('position') as THREE.BufferAttribute;
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), z = p.getZ(i);
    p.setY(i, Math.sin(x * 2.1 + z * 1.3) * 0.12 + Math.sin(z * 3.7) * 0.06);
  }
  g.computeVertexNormals();
  return g;
}
